/**
 * Offline Status Indicator Component
 * Shows network status and sync queue info in POS top bar
 */
import React from 'react';
import { useNetworkStatus, useSyncTrigger } from '../lib/useNetworkStatus';
import { useAuth } from '../context/AuthContext';
import { Wifi, WifiOff, RefreshCw, CloudOff, Check, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const OfflineStatusIndicator = () => {
  const { isOnline, offlineCount, syncStatus, isSyncing } = useNetworkStatus();
  const { token } = useAuth();
  const { triggerForceSync } = useSyncTrigger();

  const handleSyncClick = async () => {
    if (isOnline && token && offlineCount.pending > 0) {
      await triggerForceSync(token);
    }
  };

  // Render sync status message
  const getSyncMessage = () => {
    switch (syncStatus.type) {
      case 'SYNC_START':
        return 'Starting sync...';
      case 'SYNCING':
        return `Syncing ${syncStatus.current}/${syncStatus.total}...`;
      case 'SYNC_COMPLETE':
        if (syncStatus.failed > 0) {
          return `Synced ${syncStatus.synced}, ${syncStatus.failed} failed`;
        }
        return `${syncStatus.synced} sale(s) synced`;
      case 'SYNC_ERROR':
        return `Sync error: ${syncStatus.error}`;
      case 'RETRY_SCHEDULED':
        return `Retry in ${syncStatus.delay}s`;
      default:
        return null;
    }
  };

  const syncMessage = getSyncMessage();

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2" data-testid="offline-status-indicator">
        {/* Network Status Badge */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
              isOnline 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {isOnline ? (
                <Wifi className="w-3.5 h-3.5" />
              ) : (
                <WifiOff className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {isOnline 
              ? 'Connected to server' 
              : 'Working offline - Sales will sync when connected'}
          </TooltipContent>
        </Tooltip>

        {/* Pending Sales Indicator */}
        {offlineCount.pending > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSyncClick}
                disabled={!isOnline || isSyncing}
                className="h-7 gap-1.5 text-xs"
                data-testid="sync-button"
              >
                {isSyncing ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CloudOff className="w-3.5 h-3.5" />
                )}
                <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                  {offlineCount.pending}
                </Badge>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isSyncing 
                ? syncMessage || 'Syncing...'
                : isOnline 
                  ? `${offlineCount.pending} sale(s) pending sync - Click to sync now`
                  : `${offlineCount.pending} sale(s) will sync when online`}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Failed Sales Warning */}
        {offlineCount.failed > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{offlineCount.failed}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {offlineCount.failed} sale(s) failed to sync - Will retry automatically
            </TooltipContent>
          </Tooltip>
        )}

        {/* Sync Complete Message (temporary) */}
        {syncStatus.type === 'SYNC_COMPLETE' && syncStatus.synced > 0 && offlineCount.pending === 0 && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs animate-pulse">
            <Check className="w-3.5 h-3.5" />
            <span>Synced</span>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default OfflineStatusIndicator;
